import type { TrackedRect } from "./useTrackedRect";

// Selectors for "floating containers" — a target inside one of these should
// have the WHOLE container exposed by the spotlight cutout, so the popover /
// dropdown / dialog it lives in is never dimmed away.
const FLOATING_ANCESTOR =
  '[data-radix-popper-content-wrapper],[role="dialog"],[role="menu"],[role="listbox"]';

// Any floating surface a guided interaction can pop open — Radix poppers
// (popover / select / dropdown-menu, wrapped in the popper content wrapper),
// react-aria dialogs (the date-picker calendar is a `role="dialog"`), plain
// menus / listboxes, and the app's own portal dropdowns (sender / recipient /
// product pickers carry `data-recap-portal`). These are portaled to the
// document root at assorted z-indices — some BELOW the guide's dim backdrop
// (Radix content is `z-50`, the calendar `z-100`), so unless the cutout grows
// to include them they get dimmed away. The guide's own layers never match
// this selector (the caption is `role="status"`), but exclude them defensively.
const FLOATING_SURFACE =
  '[data-radix-popper-content-wrapper],[role="dialog"],[role="menu"],[role="listbox"],[data-recap-portal]';

/** Nearest floating container of an element, or the element itself. */
export function spotlightElementFor(el: HTMLElement): HTMLElement {
  return el.closest<HTMLElement>(FLOATING_ANCESTOR) ?? el;
}

/**
 * Currently-open floating surfaces (popovers / dropdowns / calendars) in the
 * document, so the guide cutout can widen to keep them visible instead of
 * dimming them. Only visible, non-guide elements with a real box are returned.
 */
export function openFloatingElements(): HTMLElement[] {
  const nodes = document.querySelectorAll<HTMLElement>(FLOATING_SURFACE);
  const out: HTMLElement[] = [];
  for (const el of nodes) {
    if (el.closest('[data-testid^="guide-"]')) continue;
    if (getComputedStyle(el).visibility === "hidden") continue;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    out.push(el);
  }
  return out;
}

/** Computed top-left border radius in px (0 on failure). */
export function readRadius(el: HTMLElement): number {
  const raw = getComputedStyle(el).borderTopLeftRadius;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Padding proportional to the element — tight around a small button, roomier
 * around a large list — so the ring/cutout adapt to each step's target rather
 * than a fixed gap. `tight` (used for the cutout of a big popover) keeps the
 * hole close to the container.
 */
export function adaptivePadding(rect: TrackedRect, tight = false): number {
  const minSide = Math.min(rect.width, rect.height);
  if (tight) return Math.round(Math.min(8, Math.max(2, minSide * 0.02)));
  return Math.round(Math.min(14, Math.max(4, minSide * 0.05)));
}
